// Cadastro da hierarquia: Grupo → Usina → UG → Regulador. Cada nível mostra as ações que o
// usuário pode fazer ali (a API confere de novo; aqui é só para não oferecer o impossível).

import { Building2, ChevronDown, ChevronRight, Cpu, Factory, Pencil, Plus, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, EmptyState, Field, Input, Modal, PageHeader, Select, Spinner } from "../../components/ui";
import { api, type NominalKey, type RegulatorDetail, type TreeGroup, type TreeSite, type TreeUnit } from "../../lib/api";
import { TIMEZONES, useAction, useTree } from "../../lib/hooks";
import { parseDecimal } from "../../lib/units";

type Dialog =
  | { kind: "group"; group?: TreeGroup }
  | { kind: "site"; groupId: number; site?: TreeSite }
  | { kind: "unit"; siteId: number; unit?: TreeUnit }
  | { kind: "regulator"; unitId: number; reg?: { id: number; slug: string; name: string } }
  | { kind: "delete"; label: string; url: string };

export function HierarchyPage() {
  const { data: tree, isLoading } = useTree();
  const [dialog, setDialog] = useState<Dialog | null>(null);

  return (
    <>
      <PageHeader
        title="Usinas e reguladores"
        description="O identificador (em cinza) de cada usina, UG e regulador é o que o gateway usa no tópico MQTT."
        actions={
          tree?.isAdmin && (
            <Button onClick={() => setDialog({ kind: "group" })}>
              <Plus className="size-4" /> Novo grupo
            </Button>
          )
        }
      />
      {isLoading ? (
        <Spinner />
      ) : !tree?.groups.length ? (
        <EmptyState title="Nada cadastrado ainda" />
      ) : (
        <div className="space-y-4">
          {tree.groups.map((g) => (
            <GroupCard key={g.id} group={g} isAdmin={tree.isAdmin} open={setDialog} />
          ))}
        </div>
      )}
      {dialog && <EditDialog dialog={dialog} isAdmin={!!tree?.isAdmin} baseDomain={tree?.baseDomain ?? ""} onClose={() => setDialog(null)} />}
    </>
  );
}

function Slug({ children }: { children: ReactNode }) {
  return <code className="rounded bg-surface-2 px-1.5 py-0.5 font-mono text-xs text-muted">{children}</code>;
}

function IconBtn({ title, onClick, children }: { title: string; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} className="rounded p-1.5 text-muted hover:bg-surface-2 hover:text-fg">
      {children}
    </button>
  );
}

function GroupCard({ group: g, isAdmin, open }: { group: TreeGroup; isAdmin: boolean; open: (d: Dialog) => void }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <Card>
      <div className="flex items-center gap-2 border-b border-line px-4 py-3">
        <button type="button" onClick={() => setExpanded(!expanded)} className="rounded p-0.5 hover:bg-surface-2" aria-label={expanded ? "Recolher" : "Expandir"}>
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <Building2 className="size-4 text-brand" aria-hidden />
        <span className="font-semibold">{g.name}</span>
        <Slug>{g.slug}</Slug>
        {g.subdomain && <Badge>{g.subdomain}.*</Badge>}
        {isAdmin && (
          <Badge tone={g.analysisMonthlyLimit ? "brand" : "neutral"}>
            {g.analysisMonthlyLimit ? `IA: ${g.analysisMonthlyLimit}/mês` : "IA desligada"}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-1">
          {g.canManage && (
            <>
              <IconBtn title="Editar grupo" onClick={() => open({ kind: "group", group: g })}>
                <Pencil className="size-4" />
              </IconBtn>
              <Button size="sm" variant="secondary" onClick={() => open({ kind: "site", groupId: g.id })}>
                <Plus className="size-3.5" /> Usina
              </Button>
            </>
          )}
          {isAdmin && g.sites.length === 0 && (
            <IconBtn title="Remover grupo" onClick={() => open({ kind: "delete", label: `o grupo ${g.name}`, url: `/api/admin/groups/${g.id}` })}>
              <Trash2 className="size-4" />
            </IconBtn>
          )}
        </div>
      </div>
      {expanded && (
        <div className="divide-y divide-line">
          {g.sites.length === 0 && <p className="px-4 py-3 text-sm text-muted">Sem usinas.</p>}
          {g.sites.map((s) => (
            <div key={s.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <Factory className="size-4 text-muted" aria-hidden />
                <span className="font-medium">{s.name}</span>
                <Slug>{s.slug}</Slug>
                <span className="text-xs text-muted">{s.timezone}</span>
                <div className="ml-auto flex items-center gap-1">
                  {s.canManage && (
                    <>
                      <IconBtn title="Editar usina" onClick={() => open({ kind: "site", groupId: g.id, site: s })}>
                        <Pencil className="size-4" />
                      </IconBtn>
                      <Button size="sm" variant="ghost" onClick={() => open({ kind: "unit", siteId: s.id })}>
                        <Plus className="size-3.5" /> UG
                      </Button>
                    </>
                  )}
                  {g.canManage && s.units.length === 0 && (
                    <IconBtn title="Remover usina" onClick={() => open({ kind: "delete", label: `a usina ${s.name}`, url: `/api/admin/sites/${s.id}` })}>
                      <Trash2 className="size-4" />
                    </IconBtn>
                  )}
                </div>
              </div>
              <ul className="mt-2 space-y-2 pl-6">
                {s.units.map((u) => (
                  <li key={u.id} className="rounded-md border border-line p-2.5">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="font-medium">{u.name}</span>
                      <Slug>{u.slug}</Slug>
                      <div className="ml-auto flex items-center gap-1">
                        {s.canManage && (
                          <>
                            <IconBtn title="Renomear UG" onClick={() => open({ kind: "unit", siteId: s.id, unit: u })}>
                              <Pencil className="size-3.5" />
                            </IconBtn>
                            <Button size="sm" variant="ghost" onClick={() => open({ kind: "regulator", unitId: u.id })}>
                              <Plus className="size-3.5" /> Regulador
                            </Button>
                            {u.regulators.length === 0 && (
                              <IconBtn title="Remover UG" onClick={() => open({ kind: "delete", label: `a unidade ${u.name}`, url: `/api/admin/units/${u.id}` })}>
                                <Trash2 className="size-3.5" />
                              </IconBtn>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {u.regulators.length > 0 && (
                      <ul className="mt-2 space-y-1 pl-4">
                        {u.regulators.map((r) => (
                          <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                            <Cpu className="size-3.5 text-muted" aria-hidden />
                            <span>{r.name}</span>
                            <Slug>
                              {s.slug}/{u.slug}/{r.slug}
                            </Slug>
                            {r.online ? <Badge tone="ok">online</Badge> : r.online === false ? <Badge tone="warn">sem link</Badge> : <Badge>sem dados</Badge>}
                            {s.canManage && (
                              <span className="ml-auto flex">
                                <IconBtn title="Editar regulador" onClick={() => open({ kind: "regulator", unitId: u.id, reg: r })}>
                                  <Pencil className="size-3.5" />
                                </IconBtn>
                                <IconBtn title="Remover regulador" onClick={() => open({ kind: "delete", label: `o regulador ${r.name}`, url: `/api/admin/regulators/${r.id}` })}>
                                  <Trash2 className="size-3.5" />
                                </IconBtn>
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// Nominais do regulador, na unidade de engenharia de costume (o banco guarda SI).
const NOMINAL_FIELDS: { key: NominalKey; label: string; unit: string; scale: number }[] = [
  { key: "voltage", label: "Tensão de armadura", unit: "kV", scale: 1e3 },
  { key: "current", label: "Corrente de armadura", unit: "A", scale: 1 },
  { key: "power", label: "Potência aparente", unit: "MVA", scale: 1e6 },
  { key: "fieldVoltage", label: "Tensão de campo", unit: "V", scale: 1 },
  { key: "fieldCurrent", label: "Corrente de campo", unit: "A", scale: 1 },
  { key: "speed", label: "Rotação", unit: "rpm", scale: 1 },
];
const fmtNominal = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 3 });

function EditDialog({ dialog, isAdmin, baseDomain, onClose }: { dialog: Dialog; isAdmin: boolean; baseDomain: string; onClose: () => void }) {
  const { run, busy, error } = useAction();
  const editing =
    (dialog.kind === "group" && dialog.group) ||
    (dialog.kind === "site" && dialog.site) ||
    (dialog.kind === "unit" && dialog.unit) ||
    (dialog.kind === "regulator" && dialog.reg) ||
    null;
  const [name, setName] = useState(editing ? editing.name : "");
  const [slug, setSlug] = useState("");
  const [timezone, setTimezone] = useState(dialog.kind === "site" && dialog.site ? dialog.site.timezone : "America/Sao_Paulo");
  // Nominal do regulador: vazio = automático (primários de TP/TC lidos do equipamento).
  const regId = dialog.kind === "regulator" && dialog.reg ? dialog.reg.id : null;
  const detail = useQuery({
    queryKey: ["regulator", regId],
    queryFn: () => api.get<RegulatorDetail>(`/api/regulators/${regId}`),
    enabled: regId !== null,
  });
  const [nominal, setNominal] = useState<Partial<Record<NominalKey, string>>>({});
  useEffect(() => {
    const manual = detail.data?.nominalManual;
    if (!manual) return;
    setNominal(Object.fromEntries(NOMINAL_FIELDS.filter((f) => manual[f.key]).map((f) => [f.key, fmtNominal(manual[f.key]! / f.scale)])));
  }, [detail.data]);
  const [subdomain, setSubdomain] = useState(dialog.kind === "group" && dialog.group ? (dialog.group.subdomain ?? "") : "");
  const [aiLimit, setAiLimit] = useState(String(dialog.kind === "group" && dialog.group ? dialog.group.analysisMonthlyLimit : 30));

  if (dialog.kind === "delete") {
    return (
      <Modal
        open
        onClose={onClose}
        title="Confirmar remoção"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button variant="danger" loading={busy} onClick={async () => (await run(() => api.del(dialog.url))) && onClose()}>
              Remover
            </Button>
          </>
        }
      >
        {error && <Alert kind="error">{error}</Alert>}
        <p className="text-sm">Remover {dialog.label}? Esta ação não pode ser desfeita.</p>
      </Modal>
    );
  }

  const what = { group: "grupo", site: "usina", unit: "unidade geradora", regulator: "regulador" }[dialog.kind];

  async function submit(e: FormEvent) {
    e.preventDefault();
    const ok = await run(async () => {
      switch (dialog.kind) {
        case "group":
          if (!dialog.group) return api.post("/api/admin/groups", { slug, name });
          // A cota de IA é recurso contratado: só o admin da plataforma altera.
          return api.patch(
            `/api/admin/groups/${dialog.group.id}`,
            isAdmin ? { name, analysisMonthlyLimit: Number(aiLimit), subdomain: subdomain || null } : { name },
          );
        case "site":
          return dialog.site
            ? api.patch(`/api/admin/sites/${dialog.site.id}`, { name, timezone })
            : api.post(`/api/admin/groups/${dialog.groupId}/sites`, { slug, name, timezone });
        case "unit":
          return dialog.unit ? api.patch(`/api/admin/units/${dialog.unit.id}`, { name }) : api.post(`/api/admin/sites/${dialog.siteId}/units`, { slug, name });
        case "regulator":
          if (!dialog.reg) return api.post(`/api/admin/units/${dialog.unitId}/regulators`, { slug, name });
          return api.patch(`/api/admin/regulators/${dialog.reg.id}`, {
            name,
            nominal: Object.fromEntries(
              NOMINAL_FIELDS.map((f) => {
                const v = parseDecimal(nominal[f.key] ?? "");
                return [f.key, v === null ? null : v * f.scale];
              }),
            ),
          });
      }
    });
    if (ok) onClose();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Editar ${what}` : `${dialog.kind === "group" || dialog.kind === "regulator" ? "Novo" : "Nova"} ${what}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="edit-form" loading={busy}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="edit-form" onSubmit={submit} className="space-y-4">
        {error && <Alert kind="error">{error}</Alert>}
        <Field label="Nome">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} required autoFocus />}</Field>
        {!editing && (
          <Field
            label="Identificador"
            hint={
              dialog.kind === "site"
                ? "Minúsculas, dígitos e hífen. É o site: do gateway e não muda depois."
                : "Minúsculas, dígitos e hífen. Não muda depois."
            }
          >
            {(id) => (
              <Input
                id={id}
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                required
                pattern="[a-z0-9][a-z0-9-]*"
                className="font-mono"
              />
            )}
          </Field>
        )}
        {dialog.kind === "group" && dialog.group && isAdmin && (
          <>
            <Field
              label="Subdomínio (white label)"
              hint={
                baseDomain
                  ? `Endereço próprio do grupo, com a marca dele já no login: ${subdomain || "<subdomínio>"}.${baseDomain}. Em branco, só pelo endereço principal.`
                  : "Esta instalação não tem domínio base (BASE_DOMAIN): o endereço do grupo é /login?grupo=<identificador>."
              }
            >
              {(id) => (
                <Input
                  id={id}
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  pattern="[a-z0-9][a-z0-9-]*"
                  className="font-mono"
                  placeholder="ex.: quantas"
                />
              )}
            </Field>
            <Field label="Análises com IA por mês" hint="Laudos, relatórios e perguntas ao Claude que o grupo pode pedir por mês. 0 desliga o recurso.">
              {(id) => <Input id={id} type="number" min={0} max={100000} step={1} value={aiLimit} onChange={(e) => setAiLimit(e.target.value)} required />}
            </Field>
          </>
        )}
        {regId !== null && (
          <fieldset className="space-y-3 rounded-md border border-line p-3">
            <legend className="px-1 text-sm font-medium">Valores nominais</legend>
            <p className="text-xs text-muted">
              Faixa dos mostradores do painel. Em branco, vale o automático: os primários de TP/TC lidos do equipamento.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {NOMINAL_FIELDS.map((f) => {
                const auto = detail.data?.nominal[f.key];
                const autoText = auto && auto.source !== "manual" ? `${fmtNominal(auto.value / f.scale)} (auto)` : "automático";
                return (
                  <Field key={f.key} label={`${f.label} (${f.unit})`}>
                    {(id) => (
                      <Input
                        id={id}
                        inputMode="decimal"
                        placeholder={autoText}
                        value={nominal[f.key] ?? ""}
                        onChange={(e) => setNominal({ ...nominal, [f.key]: e.target.value })}
                        pattern="[0-9.,]*"
                      />
                    )}
                  </Field>
                );
              })}
            </div>
          </fieldset>
        )}
        {dialog.kind === "site" && (
          <Field label="Fuso horário" hint="Converte a hora do relógio do regulador (oscilografia e eventos).">
            {(id) => (
              <Select id={id} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((tz) => (
                  <option key={tz}>{tz}</option>
                ))}
              </Select>
            )}
          </Field>
        )}
      </form>
    </Modal>
  );
}
